import {
  Badge,
  ActionConfirmationModal,
  Button,
  Card,
  CardContent,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui";
import { MatterSubItemTasksModal } from "@/components/features/matter/tasks/MatterSubItemTasksModal";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import toast from "react-hot-toast";
import { useUser } from "@/components/UserProvider";
import {
  matterPhaseApplicationOptionQueryOptions,
  matterPhasesQueryOptions,
  useApplyMatterPhaseProject,
  useClearMatterPhaseActionManualStatus,
  useClearMatterPhaseManualStatus,
  useRetryMatterPhaseAction,
  useRemoveMatterPhaseProject,
  useSetMatterPhaseActionManualStatus,
  useSetMatterPhaseActionCompletion,
  useSetMatterPhaseManualStatus,
} from "@/lib/data/react-query/matters/matter-phase";
import { useMatterPhaseLiveUpdates } from "@/lib/data/live-updates/matter-phases";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
  MATTER_PHASE_PERMISSION_KEYS,
  PAGE_PERMISSION_KEYS,
} from "@/lib/data/static/permissionOptions";
import type { Matter, MatterPhase, MatterPhaseAction } from "@/lib/types";
import { PHASE_WIDGET_CATALOG } from "@/lib/data/static/phase-widget-catalog";
import { MatterPhaseInformationWidget } from "./MatterPhaseInformationWidget";
import { cn } from "@/lib/utils/cn";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import dayjs from "dayjs";
import { formatDateTime } from "@/lib/utils/format";
import { Clock3, RotateCcw, ShieldAlert, Trash2, XCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { MatterPhaseTeamRow } from "./MatterPhaseTeamRow";
import { MatterPhaseActionCard } from "./MatterPhaseActionCard";
import { MatterPhaseProgressRing } from "./MatterPhaseProgressRing";
import {
  MatterPhaseStatusModal,
  type MatterPhaseStatusCommand,
} from "./MatterPhaseStatusModal";

const phaseStatusStyle: Record<
  MatterPhase["effectiveStatus"],
  { ring: string; text: string }
> = {
  NotStarted: {
    ring: "#d1d5db",
    text: "text-muted-foreground",
  },
  Active: {
    ring: "#f59e0b",
    text: "text-amber-800 dark:text-amber-400",
  },
  Blocked: {
    ring: "#ef4444",
    text: "text-red-700 dark:text-red-400",
  },
  Complete: {
    ring: "#10b981",
    text: "text-emerald-700 dark:text-emerald-400",
  },
  Closed: {
    ring: "#64748b",
    text: "text-slate-700 dark:text-slate-300",
  },
};

const chooseInitialPhase = (phases: MatterPhase[]): MatterPhase | undefined =>
  phases.find((phase) => phase.effectiveStatus === "Active") ??
  phases.find((phase) => phase.needsAttention) ??
  phases.find(
    (phase) =>
      phase.effectiveStatus !== "Complete" &&
      phase.effectiveStatus !== "Closed",
  ) ??
  phases.at(0);

function MatterPhaseLiveUpdatesConnection({ matterId }: { matterId: string }) {
  const { status, reconnect } = useMatterPhaseLiveUpdates(matterId);
  if (status === "ready" || status === "connecting") return null;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs text-amber-900"
    >
      <span>
        {status === "degraded"
          ? "Live updates are paused. Phase and widget information may be out of date."
          : "Reconnecting live updates. Phase and widget information may be out of date."}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={status === "resyncing"}
        onClick={reconnect}
      >
        <RotateCcw className="mr-2 h-3.5 w-3.5" /> Reconnect
      </Button>
    </div>
  );
}

export function MatterPhaseBar({
  matterId,
  matter,
}: {
  matterId: string;
  matter?: Matter;
}) {
  const { hasGrant, hasPagePermission, hasActionPermission } = useUser();
  const requestedActionId = useSearch({
    strict: false,
    select: (search) =>
      "phaseActionId" in search && typeof search.phaseActionId === "string"
        ? search.phaseActionId
        : undefined,
  });
  const { openTaskModal } = useGlobalModal();
  const canViewTasks = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailTasks,
  );
  const canCreateTasks =
    canViewTasks &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matter,
      ACTION_PERMISSION_VALUES.update,
    );
  const [taskAction, setTaskAction] = useState<{
    id: string;
    definitionId: string;
    title: string;
  }>();
  const canView = hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailPhases);
  const canOverride = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.matterPhaseOverride,
    "update",
  );
  const canRetry = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.matterPhaseEvaluation,
    "update",
  );
  const query = useQuery(matterPhasesQueryOptions(matterId, canView));
  const canApply = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.matterPhaseApplication,
    "create",
  );
  const canRemove = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.matterPhaseApplication,
    "delete",
  );
  const applicationOptionQuery = useQuery(
    matterPhaseApplicationOptionQueryOptions(
      matterId,
      canView && canApply && query.isSuccess && query.data.data === null,
    ),
  );
  const applyProject = useApplyMatterPhaseProject();
  const removeProject = useRemoveMatterPhaseProject();
  const [removeProjectOpen, setRemoveProjectOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string>();
  const applicationIntent = useRef<{
    matterId: string;
    projectRevisionId: string;
    idempotencyKey: string;
  }>();
  const setPhaseStatus = useSetMatterPhaseManualStatus();
  const clearPhaseStatus = useClearMatterPhaseManualStatus();
  const setActionStatus = useSetMatterPhaseActionManualStatus();
  const clearActionStatus = useClearMatterPhaseActionManualStatus();
  const retryAction = useRetryMatterPhaseAction();
  const setActionCompletion = useSetMatterPhaseActionCompletion();
  const phases = useMemo(
    () =>
      [...(query.data?.data?.currentGeneration.phases ?? [])].sort(
        (a, b) => a.displayPosition - b.displayPosition,
      ),
    [query.data?.data?.currentGeneration.phases],
  );
  const [selectedPhaseId, setSelectedPhaseId] = useState<string>();
  const requestedPhaseId = phases.find((phase) =>
    phase.actions.some((action) => action.id === requestedActionId),
  )?.id;
  useEffect(() => {
    if (requestedPhaseId) setSelectedPhaseId(requestedPhaseId);
  }, [requestedPhaseId]);
  useEffect(() => {
    if (!taskAction || query.isFetching || !query.isSuccess) return;
    const replacement = phases
      .flatMap((phase) => phase.actions)
      .find(
        (action) =>
          action.source.actionDefinitionId === taskAction.definitionId,
      );
    if (!replacement) {
      setTaskAction(undefined);
      toast(
        "This phase action is no longer active. Retained tasks remain available in the matter Tasks view.",
      );
    } else if (
      replacement.id !== taskAction.id ||
      replacement.title !== taskAction.title
    ) {
      setTaskAction({
        id: replacement.id,
        definitionId: replacement.source.actionDefinitionId,
        title: replacement.title,
      });
    }
  }, [phases, query.isFetching, query.isSuccess, taskAction]);
  useEffect(() => {
    if (requestedActionId && selectedPhaseId === requestedPhaseId) {
      document
        .getElementById(`phase-action-${requestedActionId}`)
        ?.scrollIntoView({ block: "center" });
    }
  }, [requestedActionId, requestedPhaseId, selectedPhaseId]);
  const [statusCommand, setStatusCommand] =
    useState<MatterPhaseStatusCommand>();
  const statusMutationPending =
    setPhaseStatus.isPending ||
    clearPhaseStatus.isPending ||
    setActionStatus.isPending ||
    clearActionStatus.isPending;
  const applicationOptions = useMemo(
    () => applicationOptionQuery.data?.data ?? [],
    [applicationOptionQuery.data?.data],
  );
  const selectedApplicationOption = applicationOptions.find(
    (option) => option.projectId === selectedProjectId,
  );

  useEffect(() => {
    if (
      selectedPhaseId &&
      phases.some((phase) => phase.id === selectedPhaseId)
    ) {
      return;
    }
    setSelectedPhaseId(
      requestedActionId ? requestedPhaseId : chooseInitialPhase(phases)?.id,
    );
  }, [phases, selectedPhaseId, requestedActionId, requestedPhaseId]);

  useEffect(() => {
    if (
      selectedProjectId &&
      !applicationOptions.some(
        (option) => option.projectId === selectedProjectId,
      )
    ) {
      setSelectedProjectId(undefined);
    }
  }, [applicationOptions, selectedProjectId]);

  if (!canView) return null;

  if (query.isLoading) {
    return (
      <Card className="overflow-hidden">
        <CardContent className="flex min-h-36 items-center justify-center">
          <LoadingSpinner className="h-6 w-6" />
        </CardContent>
      </Card>
    );
  }

  if (query.isError) {
    return (
      <Card className="border-amber-200 bg-amber-50/40">
        <CardContent className="flex items-center justify-between gap-4 py-5">
          <div>
            <Text variant="body">Matter phases could not be loaded</Text>
            <Text variant="caption">
              The rest of the Matter is still available.
            </Text>
          </div>
          <Button variant="outline" onClick={() => void query.refetch()}>
            <RotateCcw className="mr-2 h-4 w-4" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const application = query.data?.data;
  if (!application) {
    return (
      <Card className="border-border/60 border-dashed">
        <MatterPhaseLiveUpdatesConnection matterId={matterId} />
        <CardContent className="flex flex-wrap items-center justify-between gap-4 py-6">
          <div>
            <Text variant="body">Matter phases</Text>
            <Text variant="caption">
              No phase project has been applied to this matter.
            </Text>
            {canApply && applicationOptionQuery.isError ? (
              <Text variant="caption" className="mt-1 text-amber-800">
                Available phase projects could not be loaded.
              </Text>
            ) : canApply &&
              applicationOptionQuery.isSuccess &&
              applicationOptions.length === 0 ? (
              <Text variant="caption" className="mt-1">
                No active phase projects are available.
              </Text>
            ) : null}
          </div>
          {canApply && applicationOptionQuery.isError ? (
            <Button
              variant="outline"
              disabled={applicationOptionQuery.isFetching}
              onClick={() => void applicationOptionQuery.refetch()}
            >
              <RotateCcw className="mr-2 h-4 w-4" /> Retry project lookup
            </Button>
          ) : canApply && applicationOptionQuery.isLoading ? (
            <LoadingSpinner className="h-5 w-5" />
          ) : canApply && applicationOptions.length > 0 ? (
            <div className="flex min-w-72 flex-wrap items-center justify-end gap-2">
              <Select
                value={selectedProjectId}
                onValueChange={setSelectedProjectId}
                disabled={applyProject.isPending}
              >
                <SelectTrigger className="bg-background min-w-64">
                  <SelectValue placeholder="Select a phase project" />
                </SelectTrigger>
                <SelectContent>
                  {applicationOptions.map((option) => (
                    <SelectItem key={option.projectId} value={option.projectId}>
                      {option.projectTitle}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                disabled={
                  applyProject.isPending ||
                  applicationOptionQuery.isFetching ||
                  !selectedApplicationOption
                }
                onClick={() => {
                  if (!selectedApplicationOption) return;
                  if (
                    applicationIntent.current?.matterId !== matterId ||
                    applicationIntent.current.projectRevisionId !==
                      selectedApplicationOption.projectRevisionId
                  ) {
                    applicationIntent.current = {
                      matterId,
                      projectRevisionId:
                        selectedApplicationOption.projectRevisionId,
                      idempotencyKey: crypto.randomUUID(),
                    };
                  }
                  applyProject.mutate({
                    matterId,
                    projectId: selectedApplicationOption.projectId,
                    expectedPhaseProjectRevisionId:
                      selectedApplicationOption.projectRevisionId,
                    idempotencyKey: applicationIntent.current.idempotencyKey,
                  });
                }}
              >
                Apply project
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  const selectedPhase =
    phases.find((phase) => phase.id === selectedPhaseId) ?? phases.at(0);

  const renderAction = (action: MatterPhaseAction, grouped = false) => (
    <MatterPhaseActionCard
      key={action.id}
      grouped={grouped}
      action={action}
      phaseIsClosed={selectedPhase?.effectiveStatus === "Closed"}
      taskButtonLabel={
        canViewTasks && action.taskCount != null
          ? action.taskCount > 0
            ? "View task"
            : canCreateTasks
              ? "Create task"
              : undefined
          : undefined
      }
      onTaskClick={() => {
        if ((action.taskCount ?? 0) > 0) {
          setTaskAction({
            id: action.id,
            definitionId: action.source.actionDefinitionId,
            title: action.title,
          });
        } else {
          openTaskModal({
            matterId,
            editing: false,
            initialTab: "details",
            hideMatterField: true,
            subItem: { id: action.id, type: "phase-action" },
            initialData: { title: action.title.slice(0, 100) },
          });
        }
      }}
      canOverride={canOverride}
      canRetry={canRetry}
      retryPending={retryAction.isPending}
      onRetry={() =>
        void retryAction.mutateAsync({
          matterId,
          actionId: action.id,
          idempotencyKey: crypto.randomUUID(),
        })
      }
      onSetStatus={(status) => {
        setStatusCommand({
          kind: "set",
          scope: "action",
          id: action.id,
          title: action.title,
          status,
          stateVersion: action.stateVersion,
        });
      }}
      onClearStatus={() => {
        setStatusCommand({
          kind: "clear",
          scope: "action",
          id: action.id,
          title: action.title,
          stateVersion: action.stateVersion,
        });
      }}
      completionPending={setActionCompletion.isPending}
      onToggleCompletion={(completed) => {
        setActionCompletion.mutate({
          matterId,
          actionId: action.id,
          completed,
          expectedStateVersion: action.stateVersion,
        });
      }}
    />
  );
  const groupedActionIds = new Set<string>();
  const actionWidgets = (selectedPhase?.widgets ?? [])
    .filter((widget) => widget.type === "ActionGroup")
    .map((widget) => ({
      ...widget,
      actions: widget.actionDefinitionIds.flatMap((id) => {
        const action = selectedPhase?.actions.find(
          (item) => item.source.actionDefinitionId === id,
        );
        if (!action || groupedActionIds.has(action.id)) return [];
        groupedActionIds.add(action.id);
        return [action];
      }),
    }))
    .filter((widget) => widget.actions.length > 0);

  const submitStatusCommand = async (text: string) => {
    if (!statusCommand) return;
    if (statusCommand.scope === "phase") {
      if (statusCommand.kind === "set") {
        await setPhaseStatus.mutateAsync({
          matterId,
          phaseId: statusCommand.id,
          status: statusCommand.status,
          reason: text,
          expectedStateVersion: statusCommand.stateVersion,
        });
      } else {
        await clearPhaseStatus.mutateAsync({
          matterId,
          phaseId: statusCommand.id,
          expectedStateVersion: statusCommand.stateVersion,
          ...(text ? { note: text } : {}),
        });
      }
    } else if (statusCommand.kind === "set") {
      await setActionStatus.mutateAsync({
        matterId,
        actionId: statusCommand.id,
        status: statusCommand.status,
        reason: text,
        expectedStateVersion: statusCommand.stateVersion,
      });
    } else {
      await clearActionStatus.mutateAsync({
        matterId,
        actionId: statusCommand.id,
        expectedStateVersion: statusCommand.stateVersion,
        ...(text ? { note: text } : {}),
      });
    }
    setStatusCommand(undefined);
  };

  return (
    <Card className="border-border/70 @container min-w-0 overflow-hidden shadow-sm">
      <MatterPhaseLiveUpdatesConnection matterId={matterId} />
      <div className="border-border/60 flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div>
          <Text variant="body">Matter phases</Text>
          <Text variant="caption">
            {application.currentGeneration.source.projectTitle}
          </Text>
        </div>
        {canRemove ? (
          <Button
            type="button"
            variant="outline"
            className="text-destructive"
            onClick={() => {
              setRemoveProjectOpen(true);
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Remove project
          </Button>
        ) : null}
      </div>

      {!selectedPhase ? (
        <CardContent className="py-8 text-center">
          <Text variant="subHeading">
            This applied Project does not contain any Phases.
          </Text>
        </CardContent>
      ) : (
        <>
          <div className="overflow-x-auto px-3 py-4 sm:px-5">
            <div
              className="grid gap-3"
              style={{
                minWidth:
                  phases.length * 190 + Math.max(0, phases.length - 1) * 12,
                gridTemplateColumns: `repeat(${phases.length}, minmax(190px, 1fr))`,
              }}
            >
              {phases.map((phase) => {
                const selected = phase.id === selectedPhase.id;
                const style = phaseStatusStyle[phase.effectiveStatus];
                return (
                  <div
                    key={phase.id}
                    role="group"
                    aria-label={`${phase.title} phase`}
                    className={cn(
                      "flex min-w-0 flex-col rounded-xl border",
                      selected
                        ? "border-border bg-muted/45"
                        : "hover:bg-muted/45 border-transparent",
                    )}
                  >
                    <TooltipProvider delayDuration={250}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-pressed={selected}
                            onClick={() => {
                              setSelectedPhaseId(phase.id);
                            }}
                            className="focus-visible:ring-ring flex min-h-24 w-full flex-1 items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
                          >
                            <MatterPhaseProgressRing
                              phase={phase}
                              ringColor={style.ring}
                            />
                            <span className="min-w-0">
                              <span className="text-foreground block max-w-64 text-sm font-semibold break-words">
                                {phase.title}
                              </span>
                              <span className={cn("block text-xs", style.text)}>
                                {phase.effectiveStatus.replace(
                                  "NotStarted",
                                  "Not started",
                                )}
                              </span>
                              {phase.needsAttention ? (
                                <span className="mt-1 block text-[11px] text-amber-700 dark:text-amber-400">
                                  Needs attention
                                </span>
                              ) : null}
                            </span>
                          </button>
                        </TooltipTrigger>
                        <TooltipPortal>
                          <TooltipContent
                            side="top"
                            collisionPadding={12}
                            className="max-w-[min(20rem,calc(100vw-24px))] leading-relaxed [overflow-wrap:anywhere] whitespace-normal"
                          >
                            <span className="font-medium">{phase.title}</span>
                            {phase.description ? (
                              <p className="mt-1">{phase.description}</p>
                            ) : null}
                          </TooltipContent>
                        </TooltipPortal>
                      </Tooltip>
                    </TooltipProvider>
                    {selected && canOverride ? (
                      <div className="flex flex-wrap gap-2 px-3 pb-3">
                        {phase.manualOverride ? (
                          <Button
                            size="xs"
                            variant="outline"
                            className="gap-1"
                            onClick={() => {
                              setStatusCommand({
                                kind: "clear",
                                scope: "phase",
                                id: phase.id,
                                title: phase.title,
                                stateVersion: phase.stateVersion,
                              });
                            }}
                          >
                            <RotateCcw className="h-3.5 w-3.5" /> Clear override
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="xs"
                              variant="outline"
                              className="gap-1"
                              onClick={() => {
                                setStatusCommand({
                                  kind: "set",
                                  scope: "phase",
                                  id: phase.id,
                                  title: phase.title,
                                  status: "Blocked",
                                  stateVersion: phase.stateVersion,
                                });
                              }}
                            >
                              <ShieldAlert className="h-3.5 w-3.5" /> Block
                            </Button>
                            <Button
                              size="xs"
                              variant="outline"
                              className="gap-1"
                              onClick={() => {
                                setStatusCommand({
                                  kind: "set",
                                  scope: "phase",
                                  id: phase.id,
                                  title: phase.title,
                                  status: "Closed",
                                  stateVersion: phase.stateVersion,
                                });
                              }}
                            >
                              <XCircle className="h-3.5 w-3.5" /> Close
                            </Button>
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-border/60 bg-muted/15 border-t px-5 py-5">
            <MatterPhaseTeamRow
              matterId={matterId}
              generationId={application.currentGeneration.id}
              phase={selectedPhase}
            />
            {selectedPhase.manualOverrideReason ||
            selectedPhase.configurationIssueCode ||
            selectedPhase.pendingActionCount > 0 ||
            selectedPhase.failedActionCount > 0 ||
            selectedPhase.unsupportedActionCount > 0 ? (
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  {selectedPhase.manualOverrideReason ? (
                    <Text
                      variant="caption"
                      className={cn(
                        "max-w-2xl break-words",
                        phaseStatusStyle[selectedPhase.effectiveStatus].text,
                      )}
                    >
                      {selectedPhase.manualOverride}:{" "}
                      {selectedPhase.manualOverrideReason}
                      {selectedPhase.manualOverrideSetAt
                        ? ` · ${formatDateTime(dayjs.utc(selectedPhase.manualOverrideSetAt).toDate())}`
                        : ""}
                    </Text>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedPhase.configurationIssueCode ? (
                    <Badge className="border-amber-200 bg-amber-50 text-amber-800">
                      Configuration needs attention
                    </Badge>
                  ) : null}
                  {selectedPhase.pendingActionCount > 0 ? (
                    <Badge variant="secondary">
                      <Clock3 className="mr-1 h-3 w-3" />
                      {selectedPhase.pendingActionCount} pending
                    </Badge>
                  ) : null}
                  {selectedPhase.failedActionCount > 0 ? (
                    <Badge className="border-red-200 bg-red-50 text-red-700">
                      {selectedPhase.failedActionCount} failed
                    </Badge>
                  ) : null}
                  {selectedPhase.unsupportedActionCount > 0 ? (
                    <Badge className="border-amber-200 bg-amber-50 text-amber-800">
                      {selectedPhase.unsupportedActionCount} unsupported
                    </Badge>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="grid min-w-0 items-stretch gap-4 @min-[760px]:grid-cols-2">
              {(selectedPhase.widgets ?? []).map((configuredWidget) => {
                if (configuredWidget.type !== "ActionGroup") {
                  return matter ? (
                    <div
                      key={configuredWidget.id}
                      className={cn(
                        "min-w-0",
                        PHASE_WIDGET_CATALOG.find(
                          (entry) => entry.type === configuredWidget.type,
                        )?.wide && "col-span-full",
                      )}
                    >
                      <MatterPhaseInformationWidget
                        widget={configuredWidget}
                        phase={selectedPhase}
                        matter={matter}
                      />
                    </div>
                  ) : null;
                }
                const widget = actionWidgets.find(
                  (item) => item.id === configuredWidget.id,
                );
                if (!widget) return null;
                return (
                  <section
                    key={widget.id}
                    aria-label={widget.title}
                    className="border-border/70 bg-card min-w-0 overflow-hidden rounded-2xl border px-5 py-4"
                  >
                    <h3 className="text-foreground border-border/70 mb-1 border-b pb-4 text-sm font-semibold break-words">
                      {widget.title}
                    </h3>
                    {widget.actions.map((action) => renderAction(action, true))}
                  </section>
                );
              })}
              {[...selectedPhase.actions]
                .sort((a, b) => a.displayPosition - b.displayPosition)
                .filter((action) => !groupedActionIds.has(action.id))
                .map((action) => renderAction(action))}
            </div>
          </div>
        </>
      )}
      {matter && taskAction ? (
        <MatterSubItemTasksModal
          open
          onOpenChange={(open) => {
            if (!open) setTaskAction(undefined);
          }}
          matter={matter}
          entityLabel={taskAction.title}
          subItem={{ id: taskAction.id, type: "phase-action" }}
        />
      ) : null}
      {requestedActionId && query.isSuccess && !requestedPhaseId ? (
        <Text variant="caption">
          This phase action is no longer active in the current project.
        </Text>
      ) : null}
      <MatterPhaseStatusModal
        command={statusCommand}
        onOpenChange={(open) => {
          if (!open) setStatusCommand(undefined);
        }}
        onConfirm={submitStatusCommand}
        isPending={statusMutationPending}
      />
      <ActionConfirmationModal
        open={removeProjectOpen}
        setOpen={setRemoveProjectOpen}
        title="Remove phase project from this matter?"
        description="The current phases, actions, and progress will be removed from this matter. Their history remains available for audit. Applying a project again starts a fresh set of phases and actions."
        buttonText="Remove project"
        isActionPending={removeProject.isPending}
        afterActionFn={async () => {
          await removeProject.mutateAsync({
            matterId,
            applicationId: application.id,
            expectedGenerationId: application.currentGeneration.id,
          });
        }}
      />
    </Card>
  );
}
